// TODO: Overhaul — delete with the mock clients at cut-over. Shared helpers for the interim
// `ACCOUNT_OVERHAUL_CLIENT` mocks: simulated latency, the openapi-fetch success envelope, and paging.
import Decimal from 'decimal.js-light';

import { SquirrelMoneyApiContractsPaginationMetadata as Metadata } from '@core/schemas/account-overhaul-schema';

/** Simulated network latency window (ms) so loading/refresh states show. */
const MIN_LATENCY_MS = 500;
const MAX_LATENCY_MS = 1500;

/** Simulated fetch latency (ms). Mock clients wait `mockDelay(delayMs ?? randomLatencyMs())`, so a call site
 *  passing `delayMs: 0` skips it for deterministic unit tests. Under an automated browser (Playwright sets
 *  `navigator.webdriver`) it's a small fixed value — fast enough not to pace the e2e suite, but long enough
 *  that transient loading states (e.g. the modal-flow loading card during a mutation) stay observable. Real
 *  dev/eyeballing keeps the full random window. */
const AUTOMATION_LATENCY_MS = 300;
export const randomLatencyMs = (): number =>
  typeof navigator !== 'undefined' && navigator.webdriver
    ? AUTOMATION_LATENCY_MS
    : MIN_LATENCY_MS + Math.random() * (MAX_LATENCY_MS - MIN_LATENCY_MS);

/** Resolve after `ms` — used to mimic network latency in the mock clients. */
export const mockDelay = (ms: number): Promise<void> => new Promise(resolve => setTimeout(resolve, ms));

/** The openapi-fetch success envelope every mock returns. */
export const ok = <T>(data: T) => ({ data, error: undefined, response: new Response(null, { status: 200 }) });

/** Default number of equal tranches a mocked "gradual" amount progresses over. */
export const RAMP_TRANCHES = 5;
/** Default time between tranches — one tranche per minute. */
export const RAMP_TRANCHE_MS = 60_000;

/**
 * The cumulative portion of `total` that has "progressed" since `startedAtNzt`, in `tranches` equal steps of
 * one per `trancheMs`. The overhaul mocks use this to ramp a value over time instead of jumping — funds
 * moved OUT of an account (sold on the secondary market), and funds INVESTED from a pending balance. The
 * final tranche returns the *exact* `total` so cents never drift; earlier tranches are rounded to 2dp.
 * Returns 0 for a non-positive total or an unparseable start date.
 * TODO: Overhaul — mock stand-in for real server-reported progress.
 */
export function rampedAmount(
  total: number | null | undefined,
  startedAtNzt: string | null | undefined,
  now: number,
  tranches: number = RAMP_TRANCHES,
  trancheMs: number = RAMP_TRANCHE_MS,
): number {
  const amount = total ?? 0;
  if (amount <= 0) return 0;
  const started = startedAtNzt ? Date.parse(startedAtNzt) : NaN;
  if (Number.isNaN(started)) return 0;
  const completed = Math.max(0, Math.min(tranches, Math.floor((now - started) / trancheMs)));
  if (completed >= tranches) return amount;
  return new Decimal(amount).div(tranches).times(completed).toDecimalPlaces(2).toNumber();
}

/**
 * Slice `items` to a page and build the matching pagination `Metadata`. `pageSize` absent → one page of
 * everything. Shared by the list mocks (accounts, moves, transactions) so the paging shape stays identical.
 */
export function paginate<T>(items: T[], pageIndex = 1, pageSize?: number): { metadata: Metadata; items: T[] } {
  const size = pageSize && pageSize > 0 ? pageSize : items.length;
  const totalCount = items.length;
  const totalPages = totalCount === 0 ? 0 : Math.ceil(totalCount / (size || 1));
  const start = (pageIndex - 1) * size;
  return {
    metadata: {
      pageIndex,
      pageSize: size,
      totalCount,
      totalPages,
      hasPreviousPage: pageIndex > 1,
      hasNextPage: pageIndex < totalPages,
    },
    items: items.slice(start, start + size),
  };
}
